import { computed, ref, watch } from 'vue'
import type { ConfiguredPortRef } from '@gat-ship/types'
import {
  useSelectEstimatedTimeDataSource,
  useSelectLocalPortDataSource,
  useSelectPortCallTypeDataSource
} from '@gat-ship/ref-data'
import { useForm, type FieldValidators } from '@gat-ship/vue-components'
import { VESSEL_TBN, type CreatePortcallPayload } from './usePortcallApiCalls'
import {
  emptyEtaField,
  isEtaFieldComplete,
  resolveEtaField,
  validateEtaSlot,
  type EtaFieldValue,
  type EtaValidationCode
} from '../model/eta-bridge'
import {
  canSubmit as decideCanSubmit,
  isEtaSectionValid as decideEtaSectionValid,
  resolveReferenceTime
} from '../model/eta-form'

/** The editable field values backing the create-portcall form. */
export interface CreatePortcallFormValues {
  agencyId: string
  /** Vessel IMO number, or {@link VESSEL_TBN} for a to-be-named vessel. */
  vesselImo: string
  callType: string
  portOfCallLocationCode: string
  appointingPrincipal: string
  etaAtPort: EtaFieldValue
  etaAtPilotStation: EtaFieldValue
}

/** Maps an {@link EtaValidationCode} to its user-facing message. */
const ETA_MESSAGES: Record<EtaValidationCode, string> = {
  required: 'Required',
  incomplete: 'Complete the ETA or clear it',
  'at-least-one': 'Provide an ETA at the pilot station or port'
}

/**
 * Headless state + domain logic for creating a port call, shared by the
 * classic single-page form (`CreatePortcallForm.vue`) and the keyboard-first
 * step flow (`CreatePortcallStepFlow.vue`).
 *
 * Owns the form instance, the reference-data sources that drive the ETA
 * section (configured port → timezone + pilot-station flag, estimated-time
 * names, port-call types), the per-field ETA validators, and the submit
 * bridge (`getValues` → {@link CreatePortcallPayload}). It carries no
 * presentation: each view renders its own chrome and wires these pieces in.
 *
 * View-specific affordances (e.g. the classic form's ports-admin "Manage"
 * button) stay in the consuming component.
 */
export function useCreatePortcallForm() {
  // Validation is per-field (both views submit via `getValues()`, never
  // `form.handleSubmit`): the string fields use `requiredValidators`, the
  // ETA fields use the cross-field validators below.
  const form = useForm({
    defaultValues: {
      agencyId: '',
      vesselImo: '',
      callType: '',
      portOfCallLocationCode: '',
      appointingPrincipal: '',
      etaAtPort: emptyEtaField(),
      etaAtPilotStation: emptyEtaField()
    } satisfies CreatePortcallFormValues
  })

  const formValues = form.useStore((s) => s.values)

  const requiredValidators = {
    onBlur: ({ value }: { value: string }) =>
      value && value.length > 0 ? undefined : 'Required'
  }

  /** Toggles a TanStack field between a real IMO and the TBN sentinel. */
  function toggleVesselTbn(field: {
    state: { value: unknown }
    handleChange: (v: unknown) => void
    handleBlur: () => void
  }) {
    field.handleChange(field.state.value === VESSEL_TBN ? '' : VESSEL_TBN)
    field.handleBlur()
  }

  // --- Port-call types (decides radio-pills vs. searchable picker) ---
  const { portCallTypes, fetchPortCallTypes } =
    useSelectPortCallTypeDataSource()
  fetchPortCallTypes()
  const useCallTypePicker = computed(
    () => (portCallTypes.value?.length ?? 0) >= 4
  )

  // --- Local port (timezone + pilot-station flag) ---
  // The configured port drives the ETA section: `useEtaAtPilotStation`
  // decides one-vs-two ETA fields, and `timeZone` converts the chosen local
  // time to the UTC instant the API expects. Fetched reactively when the port
  // of call changes; ETA is gated until a port is picked.
  const { fetchLocalPort, loadingLocalPort } = useSelectLocalPortDataSource()
  const currentPort = ref<ConfiguredPortRef>()

  watch(
    () => formValues.value.portOfCallLocationCode,
    async (code) => {
      currentPort.value = code ? await fetchLocalPort(code) : undefined
    },
    { immediate: true }
  )

  const useEtaAtPilotStation = computed(
    () => currentPort.value?.useEtaAtPilotStation ?? false
  )
  const etaDisabled = computed(
    () => !formValues.value.portOfCallLocationCode || loadingLocalPort.value
  )

  // --- Estimated time names (resolve a picked name to a reference time) ---
  const { estimatedTimeNames, fetchEstimatedTimeNames } =
    useSelectEstimatedTimeDataSource()
  fetchEstimatedTimeNames()

  // --- ETA validation ---
  // The precision rules live in the pure `validateEtaSlot`; here we map its
  // codes to messages and wire them as per-field validators.
  function etaError(value?: EtaFieldValue, sibling?: EtaFieldValue) {
    const code = validateEtaSlot({
      value,
      sibling,
      pilotStationActive: useEtaAtPilotStation.value
    })
    return code ? ETA_MESSAGES[code] : undefined
  }

  function makeEtaValidators(sibling: () => EtaFieldValue): FieldValidators {
    const run = ({ value }: { value: string }) =>
      etaError(value as unknown as EtaFieldValue | undefined, sibling())
    return { onChange: run, onBlur: run, onSubmit: run }
  }

  const etaPortValidators = makeEtaValidators(
    () => formValues.value.etaAtPilotStation
  )
  const etaPilotValidators = makeEtaValidators(() => formValues.value.etaAtPort)

  const isEtaSectionValid = computed(() =>
    decideEtaSectionValid({
      etaAtPort: formValues.value.etaAtPort,
      etaAtPilotStation: formValues.value.etaAtPilotStation,
      pilotStationActive: useEtaAtPilotStation.value
    })
  )

  /** True once every required field holds a value and the ETA section is valid. */
  const canSubmit = computed(() =>
    decideCanSubmit({
      values: formValues.value,
      etaSectionValid: isEtaSectionValid.value
    })
  )

  function resolveSlot(slot?: EtaFieldValue) {
    return resolveEtaField(slot, {
      timeZone: currentPort.value?.timeZone,
      referenceTime: resolveReferenceTime(
        estimatedTimeNames.value ?? [],
        slot?.name
      )
    })
  }

  /**
   * Builds the {@link CreatePortcallPayload} from the current field values,
   * or `undefined` when the ETA section is invalid. Flag off: the single ETA
   * mirrors into both columns. Flag on: each ETA resolves independently.
   */
  function getValues(): CreatePortcallPayload | undefined {
    if (!isEtaSectionValid.value) return undefined
    const v = formValues.value
    const port = resolveSlot(v.etaAtPort)
    const pilot = useEtaAtPilotStation.value
      ? resolveSlot(v.etaAtPilotStation)
      : port

    return {
      agencyId: v.agencyId,
      vesselImo: v.vesselImo,
      callType: v.callType,
      portOfCallLocationCode: v.portOfCallLocationCode,
      appointingPrincipal: v.appointingPrincipal,
      etaAtPort: port?.value ?? null,
      etaAtPortPrecision: port?.precision ?? null,
      etaAtPilotStation: pilot?.value ?? null,
      etaAtPilotStationPrecision: pilot?.precision ?? null
    }
  }

  function reset() {
    form.reset()
  }

  return {
    form,
    formValues,
    requiredValidators,
    toggleVesselTbn,
    useCallTypePicker,
    loadingLocalPort,
    useEtaAtPilotStation,
    etaDisabled,
    etaPortValidators,
    etaPilotValidators,
    isEtaFieldComplete,
    canSubmit,
    getValues,
    reset
  }
}
